// SPDX-License-Identifier: AGPL-3.0-or-later

import type {Command} from '@app/features/devtools/hooks/useCommands';

const MAX_COMMANDS = 128;
const MAX_DESCRIPTION_LENGTH = 512;
const MAX_ARGS_HINT_LENGTH = 256;
const COMMAND_NAME_RE = /^[a-z][a-z0-9_-]{0,63}$/i;

export interface HermesCommandManifestEntry {
	name: string;
	description: string;
	argsHint?: string;
}

// Generated from Hermes' gateway command registry. The runtime endpoint is
// optional; this list keeps the client useful and safe when it is unavailable.
export const HERMES_COMMAND_MANIFEST: ReadonlyArray<HermesCommandManifestEntry> = [
	{
		name: 'new',
		description: 'Start a new session (fresh session ID + history)',
		argsHint: '[name]',
	},
	{
		name: 'topic',
		description: 'Enable or inspect Telegram DM topic sessions',
		argsHint: '[off|help|session-id]',
	},
	{
		name: 'save',
		description: 'Export the current conversation (bare /save shows usage)',
		argsHint: '<json|md|html> [filename] [redact]',
	},
	{
		name: 'retry',
		description: 'Retry the last message (resend to agent)',
		argsHint: '',
	},
	{
		name: 'undo',
		description: 'Back up N user turns and re-prompt (default 1)',
		argsHint: '[N]',
	},
	{
		name: 'title',
		description: 'Set a title for the current session',
		argsHint: '[name]',
	},
	{
		name: 'branch',
		description: 'Branch the current session (explore a different path)',
		argsHint: '[name]',
	},
	{
		name: 'compress',
		description:
			"Compress conversation context (add 'here [N]' to keep recent N turns; --preview shows what would happen)",
		argsHint: '[here [N] | focus topic | --preview|--dry-run]',
	},
	{
		name: 'rollback',
		description: 'List or restore filesystem checkpoints (restores keep your hand-edits; --all overrides)',
		argsHint: '[number] [--all]',
	},
	{
		name: 'stop',
		description: 'Kill all running background processes',
		argsHint: '',
	},
	{
		name: 'pause',
		description: "Pause new work globally (emergency stop); '/pause off' resumes",
		argsHint: '[reason | off]',
	},
	{
		name: 'approve',
		description: 'Approve a pending dangerous command',
		argsHint: '[session|always]',
	},
	{
		name: 'deny',
		description: 'Deny a pending dangerous command (optionally with a reason)',
		argsHint: '[all] [reason]',
	},
	{
		name: 'background',
		description: 'Run a prompt in the background',
		argsHint: '<prompt>',
	},
	{
		name: 'agents',
		description: 'Show active agents and running tasks',
		argsHint: '',
	},
	{
		name: 'queue',
		description: "Queue a prompt for the next turn (doesn't interrupt)",
		argsHint: '<prompt>',
	},
	{
		name: 'steer',
		description: 'Inject a message after the next tool call without interrupting',
		argsHint: '<prompt>',
	},
	{
		name: 'goal',
		description: 'Set a standing goal Hermes works on across turns until achieved',
		argsHint: '[text | draft <text> | show | gate add <cmd> | pause | resume | clear | status | wait <pid> | unwait]',
	},
	{
		name: 'heartbeat',
		description: 'Set a recurring prompt that re-enters this session when idle',
		argsHint: '[every <interval> <prompt> | status | pause | resume | clear]',
	},
	{
		name: 'refine',
		description: 'Review this conversation now and save lessons to memory/skills',
		argsHint: '[focus instructions]',
	},
	{
		name: 'loop',
		description: 'Re-run a prompt on a recurring interval in this session',
		argsHint: '[interval] <prompt> [--times N] [--until <condition>] | status | pause | resume | stop',
	},
	{
		name: 'moa',
		description: 'Run one prompt through the default Mixture of Agents preset, then restore your model',
		argsHint: '<prompt>',
	},
	{
		name: 'subgoal',
		description: 'Add or manage extra criteria on the active goal',
		argsHint: '[text | remove N | clear]',
	},
	{
		name: 'status',
		description: 'Show session, model, token, and context info',
		argsHint: '',
	},
	{
		name: 'egress',
		description: 'Show Docker egress proxy status',
		argsHint: '[status]',
	},
	{
		name: 'context',
		description:
			'Show detailed context window view with usage gauge, category breakdown, compression stats, and throughput',
		argsHint: '[all]',
	},
	{
		name: 'whoami',
		description: 'Show your slash command access (admin / user)',
		argsHint: '',
	},
	{
		name: 'profile',
		description: 'Show active profile name and home directory',
		argsHint: '',
	},
	{
		name: 'sethome',
		description: 'Set this chat as the home channel',
		argsHint: '',
	},
	{
		name: 'resume',
		description: 'Resume a previously-named session',
		argsHint: '[name]',
	},
	{
		name: 'sessions',
		description: 'Browse and resume previous sessions',
		argsHint: '',
	},
	{
		name: 'model',
		description: 'Switch model (session-scoped; --global to persist)',
		argsHint: '[model] [--provider name] [--global|--session] [--refresh]',
	},
	{
		name: 'codex-runtime',
		description: 'Toggle codex app-server runtime for OpenAI/Codex models',
		argsHint: '[auto|codex_app_server]',
	},
	{
		name: 'personality',
		description: 'Set a predefined personality',
		argsHint: '[name]',
	},
	{
		name: 'diff',
		description: 'Show git changes in the working directory',
		argsHint: '[staged|all|session] [--stat] [path...]',
	},
	{
		name: 'footer',
		description: 'Toggle gateway runtime-metadata footer on final replies',
		argsHint: '[on|off|status]',
	},
	{
		name: 'yolo',
		description: 'Toggle YOLO mode (skip all dangerous command approvals)',
		argsHint: '',
	},
	{
		name: 'approvals',
		description: 'Show or set the persistent dangerous-command approval mode',
		argsHint: '[manual|smart|off]',
	},
	{
		name: 'reasoning',
		description: 'Manage reasoning effort and display',
		argsHint: '[level|show|hide|full|clamp] [--global]',
	},
	{
		name: 'fast',
		description: 'Toggle fast mode — OpenAI Priority Processing / Anthropic Fast Mode (Normal/Fast)',
		argsHint: '[normal|fast|status] [--global]',
	},
	{
		name: 'voice',
		description: 'Toggle voice mode',
		argsHint: '[on|off|tts|status]',
	},
	{
		name: 'memory',
		description: 'Review pending memory writes / toggle the approval gate',
		argsHint: '[pending|approve|reject|approval] [id|on|off]',
	},
	{
		name: 'bundles',
		description: 'List skill bundles (aliases /<name> for multiple skills)',
		argsHint: '',
	},
	{
		name: 'learn',
		description: 'Learn a reusable skill from anything you describe (dirs, URLs, this chat, notes)',
		argsHint: '<what to learn from>',
	},
	{
		name: 'init',
		description: 'Generate or update AGENTS.md project instructions from a repo scan',
		argsHint: '[notes]',
	},
	{
		name: 'suggestions',
		description: 'Review suggested automations (accept/dismiss)',
		argsHint: '[accept|dismiss N | catalog]',
	},
	{
		name: 'blueprint',
		description: 'Set up an automation from a blueprint template',
		argsHint: '[name] [slot=value ...]',
	},
	{
		name: 'curator',
		description: 'Background skill maintenance (status, run, pin, archive, list-archived)',
		argsHint: '[subcommand]',
	},
	{
		name: 'kanban',
		description: 'Multi-profile collaboration board (tasks, links, comments)',
		argsHint: '[subcommand]',
	},
	{
		name: 'reload-mcp',
		description: 'Reload MCP servers from config',
		argsHint: '',
	},
	{
		name: 'reload-skills',
		description: 'Re-scan ~/.hermes/skills/ for newly installed or removed skills',
		argsHint: '',
	},
	{
		name: 'commands',
		description: 'Browse all commands and skills (paginated)',
		argsHint: '[page]',
	},
	{
		name: 'help',
		description: 'Show available commands (/help skills lists skill commands, /help <text> filters)',
		argsHint: '[skills|<filter>]',
	},
	{
		name: 'restart',
		description: 'Gracefully restart the gateway after draining active runs',
		argsHint: '',
	},
	{
		name: 'usage',
		description: 'Show token usage and rate limits; `reset` redeems a banked Codex limit reset',
		argsHint: '[reset [--force]]',
	},
	{
		name: 'topup',
		description: 'Show your Nous balance and manage billing on the portal',
		argsHint: '',
	},
	{
		name: 'insights',
		description: 'Show usage insights and analytics',
		argsHint: '[days]',
	},
	{
		name: 'platform',
		description: 'Pause, resume, or list a failing gateway platform',
		argsHint: '<pause|resume|list> [name]',
	},
	{
		name: 'update',
		description: 'Update Hermes Agent to the latest version',
		argsHint: '',
	},
	{
		name: 'version',
		description: 'Show Hermes Agent version',
		argsHint: '',
	},
	{
		name: 'debug',
		description: 'Upload debug report (system info + logs) and get shareable links',
		argsHint: '[nous|local]',
	},
];

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Validate a public command manifest without trusting its descriptions. */
export function parseHermesCommandManifest(input: unknown): Array<HermesCommandManifestEntry> {
	const raw = Array.isArray(input) ? input : isRecord(input) && Array.isArray(input.commands) ? input.commands : [];
	const result: Array<HermesCommandManifestEntry> = [];
	const seen = new Set<string>();
	for (const item of raw) {
		if (!isRecord(item) || typeof item.name !== 'string') continue;
		const name = item.name.trim().replace(/^\/+/, '');
		if (!COMMAND_NAME_RE.test(name) || seen.has(name.toLowerCase())) continue;
		const description =
			typeof item.description === 'string' && item.description.trim()
				? item.description.trim().slice(0, MAX_DESCRIPTION_LENGTH)
				: `Run /${name}`;
		const rawArgs =
			typeof item.argsHint === 'string' ? item.argsHint : typeof item.args_hint === 'string' ? item.args_hint : '';
		const argsHint = rawArgs.trim().slice(0, MAX_ARGS_HINT_LENGTH);
		seen.add(name.toLowerCase());
		result.push(argsHint ? {name, description, argsHint} : {name, description});
		if (result.length >= MAX_COMMANDS) break;
	}
	return result;
}

export function manifestToCommands(entries: ReadonlyArray<HermesCommandManifestEntry>): Array<Command> {
	return entries.map((entry) => ({
		type: 'action' as const,
		name: `/${entry.name}`,
		description: entry.argsHint ? `${entry.description} (usage: /${entry.name} ${entry.argsHint})` : entry.description,
	}));
}

let remoteManifestPromise: Promise<Array<HermesCommandManifestEntry>> | null = null;

/** Load an optional public JSON manifest; static commands remain the fallback. */
export function loadHermesCommandManifest(signal?: AbortSignal): Promise<Array<HermesCommandManifestEntry>> {
	const endpoint = (import.meta.env.PUBLIC_HERMES_COMMANDS_URL ?? '').trim();
	if (!endpoint) return Promise.resolve([...HERMES_COMMAND_MANIFEST]);
	if (remoteManifestPromise == null) {
		remoteManifestPromise = fetch(endpoint, {method: 'GET', credentials: 'omit', signal})
			.then((response) => (response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`))))
			.then((payload) => {
				const parsed = parseHermesCommandManifest(payload);
				return parsed.length > 0 ? parsed : [...HERMES_COMMAND_MANIFEST];
			})
			.catch(() => [...HERMES_COMMAND_MANIFEST]);
	}
	return remoteManifestPromise;
}
